import { useCallback, useEffect, useRef } from "react";

import { useLiveOrgPageEditorContext } from "../LiveOrgPageEditorContext";
import { OrgPageLiveEditorStepKey } from "../Steps/OrgPageLiveEditorStep.enum";

// Scroll target constants
const SCROLL_TO_TOP = "SCROLL_TO_TOP";
const SCROLL_TO_BOTTOM = "SCROLL_TO_BOTTOM";
export const ORG_PAGE_DATED_EVENTS_ID = "org-page-dated-events";
export const ORG_PAGE_SUPPORT_FORMS_ID = "org-page-support-forms";
export const ORG_PAGE_NEWSLETTERS_ID = "org-page-newsletters";

// Map step keys to element IDs in the preview (or SCROLL_TO_TOP/SCROLL_TO_BOTTOM)
const STEP_SCROLL_TARGET_MAP: Partial<Record<OrgPageLiveEditorStepKey, string>> = {
  banner: SCROLL_TO_TOP,
  logo: SCROLL_TO_TOP,
  description: SCROLL_TO_TOP,
  "main-ctas": SCROLL_TO_TOP,
  "org-information": SCROLL_TO_BOTTOM,
  events: ORG_PAGE_DATED_EVENTS_ID,
  "support-forms": ORG_PAGE_SUPPORT_FORMS_ID,
  newsletters: ORG_PAGE_NEWSLETTERS_ID,
};

/**
 * Hook to scroll the org page preview to a specific element based on the current step.
 */
export function useScrollOrgPagePreview() {
  const { iframeRef, currentStepKey } = useLiveOrgPageEditorContext();
  const isScrollingRef = useRef(false);

  const scrollToStepTarget = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow || !iframe?.contentDocument) {
      return;
    }

    const target = STEP_SCROLL_TARGET_MAP[currentStepKey];
    if (!target) {
      return;
    }

    // Prevent multiple scroll calls
    if (isScrollingRef.current) {
      return;
    }

    isScrollingRef.current = true;

    // Handle scroll to top case
    if (target === SCROLL_TO_TOP) {
      iframe.contentWindow.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else if (target === SCROLL_TO_BOTTOM) {
      iframe.contentWindow.scrollTo({
        top: iframe.contentDocument.documentElement.scrollHeight,
        behavior: "smooth",
      });
    } else {
      // Handle scroll to specific element
      const targetElement = iframe.contentDocument.getElementById(target);
      if (!targetElement) {
        isScrollingRef.current = false;
        return;
      }

      const elementTop = targetElement.getBoundingClientRect().top;
      const iframeScrollTop = iframe.contentWindow.scrollY;
      const iframeHeight = iframe.contentWindow.innerHeight;
      const elementHeight = targetElement.getBoundingClientRect().height;

      // Position the element in the middle of the viewport
      const halfViewportHeight = iframeHeight / 2;
      const scrollTarget = Math.max(iframeScrollTop + elementTop - halfViewportHeight + elementHeight / 2, 0);

      iframe.contentWindow.scrollTo({
        top: scrollTarget,
        behavior: "smooth",
      });
    }

    // Reset scrolling flag after animation
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 500);
  }, [iframeRef, currentStepKey]);

  return { scrollToStepTarget };
}

/**
 * Hook to automatically scroll the org page preview to the step target on mount.
 * Delays the scroll to ensure the preview is fully rendered.
 */
export function useScrollOrgPagePreviewOnMount() {
  const { scrollToStepTarget } = useScrollOrgPagePreview();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      scrollToStepTarget();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [scrollToStepTarget]);
}
